import { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdMobBannerHost from "@/components/AdMobBannerHost";
import { isAdMobAvailable } from "@/lib/admob";
import { openPartnerContactUrl } from "@/lib/openExternalUrl";
import baobaoLogo from "@/assets/baobao-logo.png";

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const couponBadge = (user?.usableCouponCount ?? 0) > 0;
  const hasNativeBanner = isAdMobAvailable();
  const partnerContactUrl = (import.meta.env.VITE_APP_PARTNER_CONTACT_URL ?? "").trim();
  const showPartnerFooter = partnerContactUrl.length > 0;

  return (
    <div
      className="min-h-[100dvh] flex flex-col"
      style={{
        background: "linear-gradient(160deg, #0d1f1f 0%, #1a2f2f 40%, #0f1a1a 100%)",
        fontFamily: "Noto Sans JP, sans-serif",
      }}
    >
      <header
        className="sticky top-0 z-40 px-4 flex items-center justify-between"
        style={{
          background: "rgba(13,31,31,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(212,175,55,0.2)",
          paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))",
          paddingBottom: "0.75rem",
        }}
      >
        <button
          type="button"
          className="flex items-center gap-2 text-left cursor-pointer bg-transparent border-0 p-0"
          onClick={() => navigate("/")}
        >
          <div className="w-14 h-10 flex items-center justify-center overflow-hidden rounded-md">
            <img src={baobaoLogo} alt="BaoBao ロゴ" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="text-xs font-bold leading-tight" style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif", letterSpacing: "0.05em" }}>
              Thai Relaxation
            </p>
            <p className="text-base font-bold leading-tight" style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}>
              BaoBao
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(212,175,55,0.1)" }}
            aria-label="通知"
          >
            <i className="ri-notification-3-line text-base" style={{ color: "#d4af37" }}></i>
          </button>
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(212,175,55,0.1)" }}
            onClick={() => {
              void (async () => {
                await logout();
                navigate("/login", { replace: true });
              })();
            }}
            aria-label="ログアウト"
          >
            <i className="ri-logout-box-r-line text-base" style={{ color: "#d4af37" }}></i>
          </button>
        </div>
      </header>

      <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)" }} />

      <main
        className="flex-1 overflow-y-auto pb-24"
        style={{
          paddingBottom: `calc(${showPartnerFooter ? "9.25rem" : "6rem"} + env(safe-area-inset-bottom, 0px) + ${hasNativeBanner ? "64px" : "0px"})`,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </main>

      <AdMobBannerHost />

      <nav
        className="fixed bottom-0 inset-x-0 z-40 px-4 pt-2"
        style={{
          background: "rgba(13,31,31,0.96)",
          borderTop: "1px solid rgba(212,175,55,0.2)",
          backdropFilter: "blur(12px)",
          paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))",
        }}
      >
        {showPartnerFooter && (
          <div className="max-w-lg mx-auto mb-2 px-1">
            <p
              className="text-center leading-snug mb-1.5"
              style={{ color: "rgba(244,229,195,0.55)", fontSize: "10px", letterSpacing: "0.02em" }}
            >
              このアプリをあなたのお店にも導入しませんか？導入相談はこちらから
            </p>
            <button
              type="button"
              className="w-full py-1 rounded-lg text-center cursor-pointer border-0"
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "#d4af37",
                background: "rgba(212,175,55,0.08)",
                border: "1px solid rgba(212,175,55,0.25)",
                fontFamily: "Noto Sans JP, sans-serif",
              }}
              onClick={() => void openPartnerContactUrl(partnerContactUrl)}
            >
              アプリ開発者へ連絡
            </button>
          </div>
        )}
        <div className="max-w-lg mx-auto flex gap-1.5">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `flex-1 py-3 rounded-xl text-center text-[11px] sm:text-xs font-medium transition-all ${isActive ? "font-bold" : ""}`
            }
            style={({ isActive }) => ({
              background: isActive ? "linear-gradient(90deg, #b8860b, #d4af37)" : "rgba(212,175,55,0.08)",
              color: isActive ? "#1a1a2e" : "rgba(244,229,195,0.55)",
              fontFamily: "Noto Sans JP, sans-serif",
            })}
          >
            ホーム
          </NavLink>
          <NavLink
            to="/coupons"
            className={({ isActive }) =>
              `relative flex-1 py-3 rounded-xl text-center text-[11px] sm:text-xs font-medium transition-all ${isActive ? "font-bold" : ""}`
            }
            style={({ isActive }) => ({
              background: isActive ? "linear-gradient(90deg, #b8860b, #d4af37)" : "rgba(212,175,55,0.08)",
              color: isActive ? "#1a1a2e" : "rgba(244,229,195,0.55)",
              fontFamily: "Noto Sans JP, sans-serif",
            })}
          >
            クーポン
            {couponBadge && (
              <span
                className="absolute top-2 right-2 sm:right-3 min-w-[7px] h-[7px] rounded-full"
                style={{ background: "#e85d5d", boxShadow: "0 0 0 2px rgba(13,31,31,0.96)" }}
                aria-label="利用可能なクーポンがあります"
              />
            )}
          </NavLink>
          <NavLink
            to="/history"
            className={({ isActive }) =>
              `flex-1 py-3 rounded-xl text-center text-[11px] sm:text-xs font-medium transition-all ${isActive ? "font-bold" : ""}`
            }
            style={({ isActive }) => ({
              background: isActive ? "linear-gradient(90deg, #b8860b, #d4af37)" : "rgba(212,175,55,0.08)",
              color: isActive ? "#1a1a2e" : "rgba(244,229,195,0.55)",
              fontFamily: "Noto Sans JP, sans-serif",
            })}
          >
            履歴
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
